import { ErrorMessage } from '@hookform/error-message'
import type { FunctionComponent } from 'react'
import { useEffect } from 'react'
import { useController, useFormContext } from 'react-hook-form'

import styles from './enter-address.module.scss'
import { Box } from '@components/box'
import { AddressInput } from '@components/input/address-input'
import { TextInput } from '@components/input/text-input'
import { Legend, SubLegend } from '@contentful-components/forms/legend'
import type { FormProps } from '@contentful-components/forms/props'
import { CustomFieldId, useFields } from '@graphql/hooks/field'
import { CustomFieldsetId } from '@graphql/hooks/form-page'
import { useAuthorizedStores } from '@lib/store/useStores'
import type { KycFormValues } from '@lib/utils/kyc'

const ZIP_LENGTH = 5
const MIN_LENGTH = 2
const STATE_LENGTH = MIN_LENGTH

/**
 * This form is for address validation and autocomplete.
 */
export const EnterAddress: FunctionComponent<Props> = ({ title, subtitle }) => {
    const {
        register,
        control,
        setValue,
        watch,
        formState: { errors, submitCount },
    } = useFormContext<KycFormValues>()

    const { kycStore } = useAuthorizedStores()

    const { line1, line2, city, state, zip } = useFields({
        line1: CustomFieldId.KycAddressLine1,
        line2: CustomFieldId.AddressLine2,
        city: CustomFieldId.City,
        state: CustomFieldId.State,
        zip: CustomFieldId.ZipCode,
    })

    const {
        fieldState: { invalid },
    } = useController({
        control,
        name: 'address.line1',
        rules: {
            required: {
                value: true,
                message: line1.errorTexts.required,
            },
            minLength: {
                value: MIN_LENGTH,
                message: line1.errorTexts.minLength,
            },
        },
    })

    const address = watch('address')

    useEffect(() => {
        // on keyup, convert state to uppercase
        if (address.stateProvinceRegion) {
            setValue(
                'address.stateProvinceRegion',
                address.stateProvinceRegion?.toUpperCase(),
            )
        }

        kycStore.setValues({ address })
    }, [
        address.line1,
        address.line2,
        address.city,
        address.stateProvinceRegion,
        address.zipPostalCode,
    ])

    return (
        <>
            <Legend
                preset='default'
                customFieldsetId={CustomFieldsetId.KycEnterAddress}
            >
                {title}
            </Legend>

            <SubLegend>{subtitle}</SubLegend>

            <AddressInput
                size='medium'
                preset='material'
                validationCount={submitCount}
                label={line1.label}
                placeholder={line1.placeholder}
                valid={!invalid}
                data-testid='kyc.address.line1.input'
                error={<ErrorMessage errors={errors} name='address.line1' />}
                {...register('address.line1')}
                onChange={({
                    line1,
                    city,
                    stateProvinceRegion,
                    zipPostalCode,
                }) => {
                    const options = { shouldDirty: true, shouldValidate: true }
                    if (line1) setValue('address.line1', line1, options)
                    if (city) setValue('address.city', city, options)
                    if (stateProvinceRegion)
                        setValue(
                            'address.stateProvinceRegion',
                            stateProvinceRegion.toUpperCase(),
                            options,
                        )
                    if (zipPostalCode)
                        setValue(
                            'address.zipPostalCode',
                            zipPostalCode,
                            options,
                        )
                }}
            />

            <TextInput
                size='medium'
                preset='material'
                validationCount={submitCount}
                autoComplete='address-line2'
                label={line2.label}
                valid={!errors.address?.line2}
                error={<ErrorMessage errors={errors} name='address.line2' />}
                data-testid='kyc.address.line2.input'
                placeholder={line2.placeholder}
                {...register('address.line2')}
            />
            <TextInput
                size='medium'
                preset='material'
                autoComplete='city'
                label={city.label}
                valid={!errors.address?.city}
                error={<ErrorMessage errors={errors} name='address.city' />}
                validationCount={submitCount}
                placeholder={city.placeholder}
                data-testid='kyc.address.city.input'
                {...register('address.city', {
                    required: {
                        value: true,
                        message: city.errorTexts.required,
                    },
                    minLength: {
                        value: MIN_LENGTH,
                        message: city.errorTexts.minLength,
                    },
                })}
            />

            <Box pad={[]} direction='row' className={styles.box}>
                <TextInput
                    size='medium'
                    preset='material'
                    validationCount={submitCount}
                    label={state.label}
                    valid={!errors.address?.stateProvinceRegion}
                    className={styles.state}
                    data-testid='kyc.address.state.input'
                    error={
                        <ErrorMessage
                            errors={errors}
                            name='address.stateProvinceRegion'
                        />
                    }
                    placeholder={state.placeholder}
                    maxLength={STATE_LENGTH}
                    minLength={STATE_LENGTH}
                    {...register('address.stateProvinceRegion', {
                        required: true,
                        minLength: {
                            value: STATE_LENGTH,
                            message: state.errorTexts.minLength,
                        },
                        maxLength: {
                            value: STATE_LENGTH,
                            message: state.errorTexts.maxLength,
                        },
                    })}
                />

                <div className={styles.zipbox}>
                    <TextInput
                        size='medium'
                        preset='material'
                        validationCount={submitCount}
                        autoComplete='postal-code'
                        inputMode='numeric'
                        label={zip.label}
                        className={styles.zip}
                        minLength={ZIP_LENGTH}
                        maxLength={ZIP_LENGTH}
                        data-testid='kyc.address.zip.input'
                        valid={!errors.address?.zipPostalCode}
                        error={
                            <ErrorMessage
                                errors={errors}
                                name='address.zipPostalCode'
                            />
                        }
                        placeholder={zip.placeholder}
                        {...register('address.zipPostalCode', {
                            required: {
                                value: true,
                                message: state.errorTexts.required,
                            },
                            maxLength: {
                                value: ZIP_LENGTH,
                                message: state.errorTexts.maxLength,
                            },
                            minLength: {
                                value: ZIP_LENGTH,
                                message: state.errorTexts.minLength,
                            },
                        })}
                    />
                </div>
            </Box>
        </>
    )
}

type Props = FormProps<KycFormValues>
